import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { api, ApiError, type Batch, type Idea } from '../api'
import { navigate } from '../app/routing'
import { useWakeLock } from '../app/viewport'
import { CloseIcon } from '../components/icons'
import { isFilmable } from '../domain/formats'
import { FORMAT_LABEL } from '../domain/labels'
import { loadIdeas, markLocally, useIdeas } from '../state/ideas'
import { closeQueue, loadQueue } from '../state/queue'
import { toast, toastError } from '../state/toast'
import './film.css'

/**
 * The phone as a prompter, one card at a time.
 *
 * Martin reads the card out loud and answers it on camera; `reel make` later
 * cuts the recording into one reel per spoken question. So the card is the
 * question and nothing else, as large as the screen allows, and moving on is
 * a tap anywhere or a swipe. The screen is held awake for the whole session.
 */
export function FilmSession({ batchId }: { batchId: number }) {
  const { ideas, phase } = useIdeas()
  const [batch, setBatch] = useState<Batch | null>(null)
  const [missing, setMissing] = useState(false)
  const [index, setIndex] = useState(() => readProgress(batchId))
  const [direction, setDirection] = useState<1 | -1>(1)
  const [ending, setEnding] = useState(false)

  useWakeLock(true)

  useEffect(() => {
    api
      .getBatch(batchId)
      .then(setBatch)
      .catch((error) => {
        if (error instanceof ApiError && error.status === 404) setMissing(true)
        else toastError(error, 'Could not load the batch')
      })
  }, [batchId])

  const cards: Idea[] = batch
    ? batch.idea_ids.map((id) => ideas.find((idea) => idea.id === id)).filter(readOut)
    : []
  const total = cards.length
  const done = total > 0 && index >= total

  const go = useCallback(
    (step: 1 | -1) => {
      setDirection(step)
      setIndex((current) => {
        const next = Math.max(0, Math.min(total, current + step))
        saveProgress(batchId, next)
        return next
      })
    },
    [batchId, total],
  )

  const leave = useCallback(() => navigate('/'), [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight' || event.key === ' ' || event.key === 'Enter') {
        event.preventDefault()
        go(1)
      } else if (event.key === 'ArrowLeft') go(-1)
      else if (event.key === 'Escape') leave()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [go, leave])

  const swipe = useSwipe(go)

  const finish = async () => {
    if (!batch) return
    setEnding(true)
    try {
      await api.updateBatch(batch.id, { filmed: true })
      // Filmed, the batch is a record of the session and cannot change, so it
      // stops being the next batch before its ideas move on.
      closeQueue()
      const moved = cards.filter((idea) => idea.status === 'idea' || idea.status === 'scripted')
      markLocally(
        moved.map((idea) => idea.id),
        { status: 'filmed' },
      )
      clearProgress(batch.id)
      void loadIdeas()
      void loadQueue()
      toast(`${moved.length} ${moved.length === 1 ? 'idea' : 'ideas'} moved to To post`)
      navigate('/', { replace: true })
    } catch (error) {
      toastError(error, 'Could not mark the batch filmed')
      setEnding(false)
    }
  }

  const card = cards[Math.min(index, total - 1)]

  return (
    <main className="film-session" data-series={done ? undefined : card?.series}>
      <header className="film-top">
        <button type="button" className="icon-button" aria-label="Leave session" onClick={leave}>
          <CloseIcon />
        </button>
        <span className="film-count mono" aria-live="polite">
          {total > 0 && (done ? `${total} / ${total}` : `${index + 1} / ${total}`)}
        </span>
        <span className="film-format mono">{card && !done ? FORMAT_LABEL[card.format] : ''}</span>
      </header>
      <div
        className="film-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={Math.min(index + (done ? 0 : 1), total)}
      >
        <span style={{ transform: `scaleX(${total ? Math.min(index + 1, total) / total : 0})` }} />
      </div>

      {missing && <p className="empty">This batch does not exist.</p>}
      {!missing && (!batch || phase === 'loading') && <div className="film-stage" />}

      {batch && phase !== 'loading' && !done && card && (
        <div
          className="film-stage"
          {...swipe}
          onClick={(event) => {
            // The left third goes back, the rest goes on: a thumb on the
            // right edge never has to aim.
            const { left, width } = event.currentTarget.getBoundingClientRect()
            go(event.clientX - left < width * 0.3 ? -1 : 1)
          }}
        >
          <article key={card.id} className="film-card" data-direction={direction}>
            <span className="film-rule" aria-hidden="true" />
            <FitText text={card.title} />
            {index === 0 && <p className="film-reminder">Read it out loud, then answer</p>}
          </article>
        </div>
      )}

      {batch && done && (
        <div className="film-stage film-end">
          <div className="film-card" data-direction={1}>
            <p className="film-end-kicker mono">{batch.name}</p>
            <h1 className="film-text" data-length="medium">
              That was all {total}.
            </h1>
            <p className="film-reminder">
              {batch.filmed_at
                ? 'This batch is already marked filmed.'
                : 'Ending the session moves every card to Filmed.'}
            </p>
            <div className="film-end-actions">
              {!batch.filmed_at && (
                <button
                  type="button"
                  className="button button-primary"
                  disabled={ending}
                  onClick={() => void finish()}
                >
                  End session
                </button>
              )}
              <button type="button" className="button" onClick={() => go(-1)}>
                Back to the last card
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

/** How much there is to fit: a term fills the screen, a long question still fits. */
export function lengthClass(text: string): 'short' | 'medium' | 'long' | 'longest' {
  const length = text.length
  if (length <= 16) return 'short'
  if (length <= 44) return 'medium'
  if (length <= 80) return 'long'
  return 'longest'
}

/** Starting size as a share of the card's width, by length. */
const START: Record<ReturnType<typeof lengthClass>, number> = {
  short: 0.24,
  medium: 0.175,
  long: 0.13,
  longest: 0.105,
}

/**
 * The card text at the largest size that fits.
 *
 * Starts from a size set by length, then steps down until no single word runs
 * past the edge and the whole thing fits the stage. A length rule alone gets
 * "profitability" wrong: a short question with one long word would break the
 * word in half, which is the one thing a prompter must never do mid-read.
 */
function FitText({ text }: { text: string }) {
  const ref = useRef<HTMLHeadingElement>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    const fit = () => {
      const stage = element.closest<HTMLElement>('.film-stage')
      const room = stage ? stage.clientHeight * 0.7 : Number.POSITIVE_INFINITY
      let size = Math.min(Math.round(element.clientWidth * START[lengthClass(text)]), 136)
      element.style.fontSize = `${size}px`
      while (size > 22 && (element.scrollWidth > element.clientWidth + 1 || element.offsetHeight > room)) {
        size -= 2
        element.style.fontSize = `${size}px`
      }
    }
    fit()
    // The first measurement can run before Fraunces has loaded.
    void document.fonts?.ready.then(fit)
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [text])

  return (
    <h1 ref={ref} className="film-text" data-length={lengthClass(text)}>
      {text}
    </h1>
  )
}

/**
 * Horizontal swipes step through the cards. A swipe also produces a click on
 * release, which would step a second time, so the click after a swipe is
 * swallowed.
 */
function useSwipe(go: (step: 1 | -1) => void) {
  const start = useRef<{ x: number; y: number } | null>(null)
  const swiped = useRef(false)
  return {
    onPointerDown: (event: ReactPointerEvent) => {
      start.current = { x: event.clientX, y: event.clientY }
      swiped.current = false
    },
    onPointerUp: (event: ReactPointerEvent) => {
      if (!start.current) return
      const dx = event.clientX - start.current.x
      const dy = event.clientY - start.current.y
      start.current = null
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        swiped.current = true
        go(dx < 0 ? 1 : -1)
      }
    },
    onClickCapture: (event: ReactMouseEvent) => {
      if (swiped.current) {
        swiped.current = false
        event.stopPropagation()
      }
    },
  }
}

/** A killed idea, or one cut down to LinkedIn, is not read out, whatever the batch still says. */
function readOut(idea: Idea | undefined): idea is Idea {
  return idea !== undefined && idea.status !== 'killed' && isFilmable(idea)
}

const progressKey = (batchId: number) => `content:film:${batchId}`

function readProgress(batchId: number): number {
  try {
    return Number(sessionStorage.getItem(progressKey(batchId))) || 0
  } catch {
    return 0
  }
}

function saveProgress(batchId: number, index: number): void {
  try {
    sessionStorage.setItem(progressKey(batchId), String(index))
  } catch {
    // Resuming is a convenience; filming works without it.
  }
}

function clearProgress(batchId: number): void {
  try {
    sessionStorage.removeItem(progressKey(batchId))
  } catch {
    // As above.
  }
}
